import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { fetchOrder } from "../lib/api";
import { money } from "../lib/ui";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const { data: order, isLoading, isError } = useQuery({
    queryKey: ["order", orderNumber],
    queryFn: () => fetchOrder(orderNumber),
  });

  if (isLoading) {
    return <div className="flex h-screen items-center justify-center bg-sand font-mono text-xs uppercase tracking-[0.3em] text-ink/40">Loading…</div>;
  }
  if (isError || !order) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-sand">
        <p className="font-display text-4xl text-ink">Order not found.</p>
        <Link to="/" className="font-mono text-xs uppercase tracking-[0.2em] text-rust">← Back home</Link>
      </div>
    );
  }

  return (
    <div data-testid="order-confirmation" className="min-h-screen bg-sand pt-40">
      <div className="mx-auto max-w-3xl px-6 pb-32 md:px-12">
        <motion.div
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-forest text-forest"
        >
          <Check className="h-10 w-10" strokeWidth={2} />
        </motion.div>

        <h1 className="mt-8 font-display text-6xl leading-[0.85] tracking-tighter text-ink md:text-8xl">
          Order<br />Received.
        </h1>
        <p className="mt-6 max-w-lg font-body text-lg leading-relaxed text-ink/70">
          Thank you, {order.full_name.split(" ")[0]}. Your research peptides are being packed with a printed
          Certificate of Analysis. A confirmation is headed to {order.email}.
        </p>

        <div className="mt-12 border-2 border-ink bg-surface p-6 md:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-dashed border-ink/30 pb-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-rust">Order Number</span>
            <span data-testid="order-number" className="font-display text-2xl text-ink">{order.order_number}</span>
          </div>
          <div className="mt-5 space-y-4">
            {order.items.map((i) => (
              <div key={i.slug} className="flex justify-between font-mono text-sm text-ink">
                <span>{i.name} <span className="text-ink/40">· {i.size} ×{i.quantity}</span></span>
                <span>{money(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 space-y-2 border-t border-line pt-4 font-mono text-xs uppercase tracking-[0.15em] text-ink/70">
            <div className="flex justify-between"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : money(order.shipping)}</span></div>
            <div className="flex justify-between pt-2 text-base text-ink"><span className="font-display text-2xl normal-case tracking-normal">Total</span><span>{money(order.total)}</span></div>
          </div>
        </div>

        <Link
          to="/#catalog"
          data-testid="continue-shopping"
          className="mt-10 inline-block border border-ink bg-transparent px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-ink hover:text-cream"
        >
          Back to the Catalog
        </Link>
      </div>
    </div>
  );
}
